// MotionPace — the client's read-only half of the speed curve (#532).
//
// WHAT THIS IS NOT: a second implementation of the algorithm. The curve is
// computed ONCE, server-side, by `recalculate_motion_segments` and persisted to
// `project.video.speedCurve` (packages/functions/shared/motion_pace.py). The
// module docstring there is explicit about why: three re-derivations of a
// chaotic slew-limited walk is three chances to disagree about what the user is
// actually going to get. So nothing here derives a curve. This module only
// READS the persisted record and reduces it to something a chart can draw.
//
// Three pieces of real arithmetic live here, and each is a port of a rule that
// already exists elsewhere in the product:
//
//  1. `speedsFromFrameIndices` — a faithful port of
//     `motion_pace.speeds_from_frame_indices`. The persisted record deliberately
//     carries no per-frame `speeds` array (a second 8-bytes-per-frame array on a
//     document budgeted at 1 MiB — see `compute_speed_curve`), so every consumer
//     recovers speed from the stride between consecutive `frameIndices`. The
//     Python side is THE canonical recovery; this is its JS twin and must agree
//     with it value for value. motionPace.test.js pins that agreement against
//     outputs generated from the Python module itself.
//
//  2. `blockFactor`/`downsampleMean`/`downsampleMax` — the same bounded-point
//     blocking the luminance chart already ships. `summarize_luminance_track`
//     (packages/containers/analyzevideo/processing/flicker.py) cuts a full-rate
//     track into blocks of `ceil(n / MAX_TRACK_POINTS)` samples, keeps the
//     trailing partial block, and stores one number per block — which is what
//     bounds that chart at 240 points regardless of source length. `frameIndices`
//     is one entry per OUTPUT frame and `motionValues` one per source PAIR, so
//     both run to tens of thousands on a real shoot; charting them raw janks the
//     editor. Same rule, same cap, applied in the browser because this data is
//     stored at full rate.
//
//  3. `maskCaptureGapPairs` — a port of `motion_pace.mask_capture_gap_pairs`,
//     applied to the motion bars for the same reason the server applies it to
//     the curve: the pair spanning a camera-off stretch scores enormous, and
//     drawing it would set `motionMax` on its own and flatten every real bar.
//
// ONE GRID FOR THE WHOLE CHART. Motion and speed are drawn against SOURCE time —
// the axis the trim chart and the source <video> already share — so both series
// are blocked with ONE factor derived from the curve's `sourceFrames`. Speed is
// per OUTPUT frame, so `speedBySourceBlock` regroups it by where in the SOURCE
// each output frame landed; under variable speed those two clocks are not
// proportional, and blocking the output array directly would smear the curve
// against the motion bars it is drawn on top of.

import { normalizeGapFrames } from './captureGaps';

// The persisted record's field on the project's video document. Mirrors
// SPEED_CURVE_FIELD in shared/motion_pace.py.
export const SPEED_CURVE_FIELD = 'speedCurve';

// --- User-facing parameter bounds ------------------------------------------
// Mirrors of the constants in shared/motion_pace.py. They are CLAMPS there and
// clamps here: an out-of-range value from an older or hand-rolled client has to
// render something sane rather than blank the card.
export const MAX_SPEED_DEFAULT = 7.0;
export const MAX_SPEED_MIN = 1.5;
export const MAX_SPEED_MAX = 15.0;

export const SMOOTHNESS_DEFAULT = 0.5;
export const SMOOTHNESS_MIN = 0.25;
export const SMOOTHNESS_MAX = 3.0;

// `smoothness` is NOT a duration, and the difference is the whole of #584.
//
// The stored setting drives two things at once — the Gaussian sigma (`sigma =
// source_fps * smoothness`) and the slew limiter's ramp — and the engine derives
// the ramp from it as `rampSeconds = RAMP_SECONDS_PER_SMOOTHNESS * smoothness`
// (`motion_pace.build_speed_curve`). So the ramp a user actually watches is
// 1.5x the number the setting holds: the 0.5 default is a 0.75s ramp, and the
// 3.0 top of the slider is a 4.5s one.
//
// Labelling the raw setting with an "s" therefore understated every position on
// the track by a third. This constant exists so the control can quote the ramp
// the engine will really apply. It changes NOTHING about what is stored or
// rendered — presentation only.
//
// MIRROR of RAMP_SECONDS_PER_SMOOTHNESS in packages/functions/shared/motion_pace.py
// (itself mirrored byte-for-byte into the render container). Pinned against that
// file by motionPaceRamp.conformance.test.js, so changing the engine's
// multiplier without changing this one fails the build's tests rather than
// silently making the label lie again.
export const RAMP_SECONDS_PER_SMOOTHNESS = 1.5;

// No frame interpolation in v1, so playback can never go below source rate.
export const SPEED_FLOOR = 1.0;

// Output frames averaged when recovering per-frame speed from `frameIndices`.
// Mirrors SPEED_RECOVERY_WINDOW; see speedsFromFrameIndices.
export const SPEED_RECOVERY_WINDOW = 9;

// The chart's point budget, equal to MAX_TRACK_POINTS in
// analyzevideo/processing/flicker.py. The luminance chart is bounded at 240
// points by the STORAGE format; this chart reads full-rate arrays, so the same
// bound is applied here instead. Same number on purpose — the two charts sit in
// the same column and a different horizontal resolution would read as one of
// them being noisier footage rather than a different downsample.
export const MAX_CHART_POINTS = 240;

// The steps the two sliders move in. Deliberately NOT a rounding rule: they size
// the control's granularity and nothing else, and no stored value is ever
// snapped to them (#454 — a slider's `step` quantized a stored full-precision
// motionThreshold on every drag). `resolveMaxSpeed`/`resolveSmoothness` clamp
// and never round, so a value written by the API or by an older client survives
// a round trip through this UI bit-identical unless the user actually moves the
// control.
export const MAX_SPEED_STEP = 0.1;
export const SMOOTHNESS_STEP = 0.05;

const isFiniteNumber = (value) =>
  typeof value === 'number' && Number.isFinite(value);

// `_finite_float` from shared/motion_pace.py, deliberately NARROWED where the
// two languages' parsers disagree. Python's float() accepts '' as an error and
// 'inf' as infinity; JS Number('') is 0 and Number('Infinity') is Infinity. Both
// of those differences would resolve a corrupt stored value to a DIFFERENT
// ceiling than the render used, so the string path only accepts what both
// languages read the same way: a plain finite decimal.
const finiteFloat = (value, fallback) => {
  if (typeof value === 'boolean' || value === null || value === undefined) {
    return fallback;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }
  if (typeof value === 'string') {
    const text = value.trim();
    if (text === '') return fallback;
    const parsed = Number(text);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
};

// `_clamp` from shared/motion_pace.py, including its NaN behaviour: neither
// comparison holds for NaN, so NaN passes through rather than becoming a bound.
const clampTo = (value, low, high) => {
  if (value < low) return low;
  if (value > high) return high;
  return value;
};

/**
 * The effective `maxSpeed`, clamped into the validated range. Mirrors
 * `resolve_max_speed`.
 *
 * CLAMPS, NEVER ROUNDS. See MAX_SPEED_STEP.
 */
export const resolveMaxSpeed = (value) =>
  clampTo(finiteFloat(value, MAX_SPEED_DEFAULT), MAX_SPEED_MIN, MAX_SPEED_MAX);

/**
 * The effective `smoothness`, clamped into the validated range. Mirrors
 * `resolve_smoothness`. Clamps, never rounds.
 */
export const resolveSmoothness = (value) =>
  clampTo(finiteFloat(value, SMOOTHNESS_DEFAULT), SMOOTHNESS_MIN, SMOOTHNESS_MAX);

/**
 * Per-output-frame playback speed, recovered from `frameIndices`.
 *
 * A FAITHFUL PORT of `motion_pace.speeds_from_frame_indices` — read that
 * docstring for the reasoning; this comment covers only what the port has to
 * get right.
 *
 * Speed IS the source-frame stride: the walk advances by the current speed and
 * emits `round(position)`, so consecutive indices differ by that speed give or
 * take the rounding. Averaging over `window` output frames takes out that +/-1
 * quantization.
 *
 * PASS `curve.maxSpeed`. The boxcar averages INTEGER strides, so it can only
 * land on multiples of 1/window; when the ceiling is not one of them the window
 * straddles it and reads HIGH — at maxSpeed 1.5 a pinned-at-the-ceiling clip
 * recovers up to 1.71x, a speed the walk clamped away and the render will never
 * play. `maxSpeed` null/undefined means "ceiling unknown" and applies NO upper
 * bound; it deliberately does not fall back to MAX_SPEED_DEFAULT, because
 * silently squashing a 12x curve to 7x would be a worse lie than the overshoot.
 *
 * `math.fsum` on the Python side is plain addition here on purpose: the strides
 * being summed are differences of integer frame indices, so every partial sum is
 * an exact float64 integer and the two agree bit for bit.
 */
export function speedsFromFrameIndices(
  frameIndices,
  sourceFrames,
  maxSpeed = null,
  window = SPEED_RECOVERY_WINDOW
) {
  const ceiling = Math.max(finiteFloat(maxSpeed, Infinity), SPEED_FLOOR);
  const indices = Array.isArray(frameIndices) ? frameIndices : [];
  const count = indices.length;
  if (count === 0) return [];
  const frames = finiteFloat(sourceFrames, 0);
  if (count === 1) {
    // Nothing to difference against, so the only evidence left is how much
    // source the single frame stood in front of.
    return [clampTo(frames - Number(indices[0]), SPEED_FLOOR, ceiling)];
  }

  const strides = new Array(count);
  for (let i = 0; i < count - 1; i += 1) {
    strides[i] = Number(indices[i + 1]) - Number(indices[i]);
  }
  // The final frame has no successor: the walk stepped PAST the end there, so
  // the source it left over is a lower bound on the speed rather than the speed.
  // Hold the previous value instead of inventing a slow last frame.
  strides[count - 1] = strides[count - 2];

  const radius = Math.max(0, Math.floor(Math.trunc(window) / 2));
  if (radius === 0) {
    return strides.map((stride) => clampTo(stride, SPEED_FLOOR, ceiling));
  }

  const averaged = new Array(count);
  for (let i = 0; i < count; i += 1) {
    const low = Math.max(0, i - radius);
    const high = Math.min(count, i + radius + 1);
    let sum = 0;
    for (let k = low; k < high; k += 1) sum += strides[k];
    averaged[i] = clampTo(sum / (high - low), SPEED_FLOOR, ceiling);
  }
  return averaged;
}

/**
 * How many samples one chart point covers, so the series never exceeds
 * `maxPoints` points.
 *
 * `max(1, ceil(n / maxPoints))` — the identical rule
 * `summarize_luminance_track` uses to bound the stored luminance track. A
 * series already inside the budget gets factor 1, which makes every block a
 * single sample and the downsample the identity.
 */
export function blockFactor(count, maxPoints = MAX_CHART_POINTS) {
  const n = isFiniteNumber(count) ? Math.floor(count) : 0;
  const cap = isFiniteNumber(maxPoints) ? Math.floor(maxPoints) : 0;
  if (n <= 0 || cap < 1) return 1;
  return Math.max(1, Math.ceil(n / cap));
}

// One block walker for both reducers, so "what a chart point covers" is defined
// once. Blocks are [start, start + factor); the trailing partial block is KEPT,
// which is what makes the point count ceil(n / factor) <= maxPoints.
const downsampleBlocks = (values, factor, reduce) => {
  if (!Array.isArray(values) || values.length === 0) return [];
  const step = isFiniteNumber(factor) && factor >= 1 ? Math.floor(factor) : 1;
  const n = values.length;
  const out = [];
  for (let start = 0; start < n; start += step) {
    out.push(reduce(values, start, Math.min(n, start + step)));
  }
  return out;
};

// A non-finite sample would poison the whole block (and with it a chart point),
// so each reducer skips them and an all-garbage block reads as 0 rather than
// NaN. Deliberately softer than `_clean_track`'s "any bad sample invalidates the
// track": this data is already stored, and one corrupt entry must not blank a
// chart that is otherwise correct.
const blockMean = (values, start, end) => {
  let sum = 0;
  let seen = 0;
  for (let i = start; i < end; i += 1) {
    const v = values[i];
    if (isFiniteNumber(v)) {
      sum += v;
      seen += 1;
    }
  }
  return seen === 0 ? 0 : sum / seen;
};

const blockMax = (values, start, end) => {
  let max = -Infinity;
  for (let i = start; i < end; i += 1) {
    const v = values[i];
    if (isFiniteNumber(v) && v > max) max = v;
  }
  return max === -Infinity ? 0 : max;
};

/** Block MEAN — for the speed curve, where the block's average IS the speed. */
export const downsampleMean = (values, factor) =>
  downsampleBlocks(values, factor, blockMean);

/**
 * Block MAX — for the motion bars.
 *
 * Same choice `buildEnvelope` (utils/motionTimeline.js) makes on the trim chart:
 * motion is spiky, and a mean would erase a short burst entirely at the factors
 * a multi-thousand-frame shoot produces. The bars are read as "was anything
 * happening here", which a max answers and a mean does not.
 */
export const downsampleMax = (values, factor) =>
  downsampleBlocks(values, factor, blockMax);

/**
 * `motionValues` with the frame pair spanning each capture gap zeroed.
 *
 * A port of `motion_pace.mask_capture_gap_pairs`, and the chart has to apply it
 * for the same reason the server does before computing the curve: a capture gap
 * is a camera-off stretch (overnight, a weekend), so the one pair that straddles
 * it compares two completely different scenes and scores enormous.
 *
 * WITHOUT THE MASK THE CHART CONTRADICTS ITSELF. That single pair would be the
 * largest entry in the array on any multi-day shoot, so it alone would set
 * `motionMax` and normalize every real motion bar to near zero — and the speed
 * curve would be drawn ACCELERATING through the one full-height bar, because the
 * server never saw it. Same array, same rule, both sides.
 *
 * `video.captureGapFrames` records the FIRST frame after each gap, so gap frame
 * `g` is the second half of pair `g - 1` and it is `motionValues[g - 1]` that
 * goes. Coercion is `normalizeGapFrames` (lib/captureGaps.js) — shared with the
 * render-side splitter and the trim chart's markers, so all three mask the same
 * set of pairs on the same document.
 *
 * Pure: the caller's array is never mutated, and is returned as-is when nothing
 * masks.
 */
export function maskCaptureGapPairs(motionValues, captureGapFrames) {
  if (!Array.isArray(motionValues) || motionValues.length === 0) return motionValues;
  const frames = normalizeGapFrames(captureGapFrames);
  if (frames.length === 0) return motionValues;

  let masked = null;
  for (const frame of frames) {
    if (frame < 1 || frame > motionValues.length) continue;
    if (!masked) masked = motionValues.slice();
    masked[frame - 1] = 0;
  }
  return masked || motionValues;
}

/**
 * Per-output-frame speeds regrouped onto the SOURCE-frame grid the chart draws.
 *
 * Both series on this chart are plotted against source time, because that is
 * the axis the source <video> and the trim chart already use. Speed is indexed
 * by OUTPUT frame, and under variable speed the two clocks are not
 * proportional — 30 output frames may cover 30 source frames or 180 — so the
 * speed array cannot simply be blocked and laid over the motion bars.
 *
 * `frameIndices[k]` is the source frame output frame k shows, so block b (source
 * frames [b*factor, (b+1)*factor)) averages the speeds of every output frame
 * whose index lands inside it. Same reducer, same block edges as
 * `downsampleMean` — this only changes what decides a sample's block.
 *
 * An empty block (possible when `factor` is smaller than the current speed, i.e.
 * the walk skipped straight over it) HOLDS the previous value rather than
 * dropping to zero: the walk was travelling at that speed through the block it
 * jumped, and a gap in the line would read as a stall.
 */
export function speedBySourceBlock(frameIndices, speeds, sourceFrames, factor) {
  const indices = Array.isArray(frameIndices) ? frameIndices : [];
  const values = Array.isArray(speeds) ? speeds : [];
  const frames = isFiniteNumber(sourceFrames) ? Math.floor(sourceFrames) : 0;
  const step = isFiniteNumber(factor) && factor >= 1 ? Math.floor(factor) : 1;
  if (frames <= 0 || indices.length === 0) return [];

  const blocks = Math.ceil(frames / step);
  const sums = new Array(blocks).fill(0);
  const counts = new Array(blocks).fill(0);
  for (let k = 0; k < indices.length; k += 1) {
    const frame = indices[k];
    const speed = values[k];
    if (!isFiniteNumber(frame) || !isFiniteNumber(speed)) continue;
    const b = Math.floor(frame / step);
    if (b < 0 || b >= blocks) continue;
    sums[b] += speed;
    counts[b] += 1;
  }

  const out = new Array(blocks);
  let held = SPEED_FLOOR;
  for (let b = 0; b < blocks; b += 1) {
    if (counts[b] > 0) held = sums[b] / counts[b];
    out[b] = held;
  }
  return out;
}

// Whether a persisted record is usable at all. `frameIndices` is the render
// instruction and everything else is a summary of it, so a record without one is
// not a curve however complete it otherwise looks.
const readCurve = (speedCurve) => {
  if (!speedCurve || typeof speedCurve !== 'object' || Array.isArray(speedCurve)) {
    return null;
  }
  const frameIndices = speedCurve.frameIndices;
  if (!Array.isArray(frameIndices) || frameIndices.length === 0) return null;
  return speedCurve;
};

/**
 * Everything the pace chart needs, or the reason it cannot be drawn.
 *
 * @param {object} speedCurve `project.video.speedCurve`, the record #528
 *   persists. Never recomputed here.
 * @param {number[]} motionValues `project.video.motionValues` — one score per
 *   source frame PAIR, the same array the trim chart plots. Optional: the speed
 *   curve alone is a complete chart, the bars are context.
 * @param {object} [options]
 * @param {number} [options.maxPoints] the chart's point budget.
 * @param {number[]} [options.captureGapFrames] `project.video.captureGapFrames`.
 *   Masked out of the bars before they are blocked — see maskCaptureGapPairs.
 * @returns {{mode: 'ready'|'unavailable', reason: string|null, ...}}
 *
 * reason (mode 'unavailable'):
 *   'no-curve'  nothing persisted yet — MotionPace was just switched on and the
 *               save that computes the curve has not landed, or the project was
 *               last saved with MotionPace off (the callable DELETEs the field
 *               in that case, so a stale curve is never shown).
 *   'empty'     a record with no output frames. Not reachable from
 *               `compute_speed_curve`, which raises rather than storing one, but
 *               the document is client-writable like every project field.
 */
export function buildMotionPacePreview(
  speedCurve,
  motionValues,
  { maxPoints = MAX_CHART_POINTS, captureGapFrames = null } = {}
) {
  const unavailable = (reason) => ({ mode: 'unavailable', reason });

  const curve = readCurve(speedCurve);
  if (!curve) return unavailable('no-curve');

  const frameIndices = curve.frameIndices;
  const sourceFrames = isFiniteNumber(curve.sourceFrames) && curve.sourceFrames > 0
    ? Math.floor(curve.sourceFrames)
    // A record missing its frame count still has the walk itself: the last
    // emitted index is a source frame that exists, so the source is at least
    // that long.
    : Math.floor(Number(frameIndices[frameIndices.length - 1]) || 0) + 1;
  if (!(sourceFrames > 0)) return unavailable('empty');

  const curveMaxSpeed = isFiniteNumber(curve.maxSpeed) ? curve.maxSpeed : null;
  const speeds = speedsFromFrameIndices(frameIndices, sourceFrames, curveMaxSpeed);

  // ONE factor for both series: the bars and the curve share an x axis, and two
  // block sizes would put a spike and the speed reaction to it in different
  // places.
  const factor = blockFactor(sourceFrames, maxPoints);
  const speedPoints = speedBySourceBlock(frameIndices, speeds, sourceFrames, factor);
  // Masked BEFORE blocking: `downsampleMax` keeps the largest sample in each
  // block, so a gap pair left in would survive the downsample and become the
  // bar the whole chart is scaled against.
  const motionPoints = Array.isArray(motionValues) && motionValues.length > 0
    ? downsampleMax(maskCaptureGapPairs(motionValues, captureGapFrames), factor)
    : null;
  const motionMax = motionPoints
    ? motionPoints.reduce((m, v) => (v > m ? v : m), 0)
    : 0;

  const sourceFps = isFiniteNumber(curve.sourceFps) && curve.sourceFps > 0
    ? curve.sourceFps
    : null;
  const outputFps = isFiniteNumber(curve.outputFps) && curve.outputFps > 0
    ? curve.outputFps
    : null;
  const outputFrames = isFiniteNumber(curve.outputFrames) && curve.outputFrames > 0
    ? Math.floor(curve.outputFrames)
    : frameIndices.length;

  return {
    mode: 'ready',
    reason: null,
    factor,
    speedPoints,
    motionPoints,
    motionMax,
    sourceFrames,
    outputFrames,
    sourceFps,
    outputFps,
    sourceSeconds: sourceFps ? sourceFrames / sourceFps : null,
    outputSeconds: outputFps ? outputFrames / outputFps : null,
    // The record's own exact summaries, computed from the true walk. Quoted
    // verbatim; never recomputed from the recovery above.
    curveMaxSpeed,
    curveSmoothness: isFiniteNumber(curve.smoothness) ? curve.smoothness : null,
    speedMin: isFiniteNumber(curve.speedMin) ? curve.speedMin : null,
    speedMax: isFiniteNumber(curve.speedMax) ? curve.speedMax : null,
    speedMean: isFiniteNumber(curve.speedMean) ? curve.speedMean : null,
    speedupVsSource: isFiniteNumber(curve.speedupVsSource) ? curve.speedupVsSource : null,
  };
}

/**
 * Whether the persisted curve was computed for the settings now on screen.
 *
 * The curve is recomputed server-side on save, so for the ~second between a
 * slider commit and the Firestore snapshot the chart is drawing the PREVIOUS
 * setting's curve. The card says so rather than presenting a stale line as the
 * answer. Compared against the record's own clamped fields, so a draft value
 * outside the legal range does not read as permanently stale.
 */
export const speedCurveMatchesSettings = (speedCurve, maxSpeed, smoothness) => {
  const curve = readCurve(speedCurve);
  if (!curve) return false;
  return (
    resolveMaxSpeed(curve.maxSpeed) === resolveMaxSpeed(maxSpeed)
    && resolveSmoothness(curve.smoothness) === resolveSmoothness(smoothness)
  );
};

/**
 * The output clip a persisted curve describes: how many frames it runs for and
 * at what rate. Null when the record cannot answer.
 *
 * This is a paced render's equivalent of "the analyzed motion segments" — the
 * span the clip chain is handed. The worker replaces the source with a paced one
 * and gives the rest of the pipeline a single full-length segment measured in
 * OUTPUT frames at `outputFps` (`video_editing.py`, the VARIABLE_SPEED branch),
 * so every length the UI states for a pace render starts here.
 *
 * Reads `outputFrames` rather than `frameIndices.length`, the same preference
 * `buildMotionPacePreview` applies: the record's own summary is the field every
 * other consumer quotes, and the two are equal on anything `compute_speed_curve`
 * wrote.
 *
 * Returns null, never a zero-length span, when the record cannot answer: a
 * project analyzed before curve seeding (#573) has MotionPace on and nothing
 * stored, and the caller falls back to the trim segment math there rather than
 * state a confident 0:00:00. Numeric strings are refused on purpose — a record
 * that did not come from `compute_speed_curve` falls back rather than being
 * coerced into a confident number.
 */
export const paceOutputSpan = (speedCurve) => {
  if (!speedCurve || typeof speedCurve !== 'object' || Array.isArray(speedCurve)) {
    return null;
  }
  const { outputFrames, outputFps } = speedCurve;
  if (!isFiniteNumber(outputFrames) || outputFrames <= 0) return null;
  if (!isFiniteNumber(outputFps) || outputFps <= 0) return null;
  return { outputFrames: Math.floor(outputFrames), outputFps };
};

// `bisect.bisect_left` — the insertion point for `target` in a sorted array,
// i.e. how many entries sit strictly below it.
const bisectLeft = (sorted, target) => {
  let low = 0;
  let high = sorted.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (sorted[mid] < target) low = mid + 1;
    else high = mid;
  }
  return low;
};

/**
 * Capture-gap and clip-join markers moved from SOURCE frames into the paced
 * OUTPUT-frame space the clip chain works in.
 *
 * A port of `pacing.remap_source_frames_to_output` (generatevideo/processing/
 * pacing.py), and it has to stay one: the worker splits the paced clip at
 * exactly these numbers, so a receipt counting a different set is describing a
 * different render.
 *
 * A gap at source frame G becomes the first OUTPUT frame showing G or later —
 * the frame the day actually changes on screen. Markers landing on the first or
 * last output frame are dropped: they are boundaries with nothing on one side.
 * Two gaps the walk sprinted over can land on the SAME output frame, which is
 * why the result is deduped rather than one entry per marker.
 *
 * `sourceFrames` goes through `normalizeGapFrames` — the same coercion the trim
 * path and the render-side splitter apply — so a paced render cannot split on a
 * bool or a string that an unpaced render of the same project drops.
 */
export const remapGapFramesToOutput = (sourceFrames, frameIndices) => {
  const indices = Array.isArray(frameIndices) ? frameIndices : [];
  const total = indices.length;
  if (total === 0) return [];
  const moved = new Set();
  for (const frame of normalizeGapFrames(sourceFrames)) {
    const position = bisectLeft(indices, frame);
    if (position > 0 && position < total) moved.add(position);
  }
  return [...moved].sort((a, b) => a - b);
};

/** "4x" / "2.5x" — a speed for display, without inventing precision. */
export const formatSpeed = (value) => {
  if (!isFiniteNumber(value)) return '';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

/** "1" / "1.5" / "0.25" — a raw smoothness SETTING for display, unitless.
 *
 * Not what the Smoothness control shows: that quotes seconds, and seconds are
 * `rampSecondsFor` below. Kept because the setting itself is still the thing
 * compared, stored and round-tripped. */
export const formatSmoothness = (value) => {
  if (!isFiniteNumber(value)) return '';
  const rounded = Math.round(value * 100) / 100;
  return String(rounded);
};

/**
 * The ramp duration the engine will actually apply, in seconds, for a given
 * smoothness setting (#584).
 *
 * Clamped first, so a slider mid-drag or a hand-rolled out-of-range value
 * reports the ramp the engine would really use rather than an impossible one.
 */
export const rampSecondsFor = (smoothness) =>
  RAMP_SECONDS_PER_SMOOTHNESS * resolveSmoothness(smoothness);

/**
 * "0.75s" / "4.5s" — the Smoothness control's readout.
 *
 * Quotes the EFFECTIVE ramp, not the stored setting, so the number on screen is
 * the number of seconds the speed takes to travel the full 1x..maxSpeed range.
 * Rounded to two decimals: the 0.05 slider step lands on values like 1.875s, and
 * printing that in full reads as false precision on a control whose whole job is
 * "about this long".
 */
export const formatRampSeconds = (smoothness) => {
  const rounded = Math.round(rampSecondsFor(smoothness) * 100) / 100;
  return `${rounded}s`;
};
